import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { API } from "@/App";
import { Calendar, MapPin, Clock, ChevronLeft, ChevronRight, Tag, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteContentCache } from "@/contexts/SiteContentCache";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_crm-news-hub/artifacts/srv2lps9_ChatGPT%20Image%20Apr%202%2C%202026%2C%2010_47_10%20AM.png";

const EVENT_TYPE_CONFIG = {
  tryout: { label: "Tryout", color: "bg-green-100 text-green-800 border-green-200" },
  game: { label: "Game", color: "bg-blue-100 text-blue-800 border-blue-200" },
  tournament: { label: "Tournament", color: "bg-purple-100 text-purple-800 border-purple-200" },
  training: { label: "Training", color: "bg-amber-100 text-amber-800 border-amber-200" },
  meeting: { label: "Meeting", color: "bg-gray-100 text-gray-700 border-gray-200" },
  general: { label: "Event", color: "bg-[#0C2E3C]/10 text-[#0C2E3C] border-[#0C2E3C]/20" },
};

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [logoUrl, setLogoUrl] = useState(LOGO_URL);
  const siteContent = useSiteContentCache();

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/events/public`);
        setEvents(res.data);
      } catch (err) {
        console.error("Failed to fetch events:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();

    const logo = siteContent?.data?.site_logo_url;
    if (logo) setLogoUrl(logo);
  }, [siteContent]);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const lastDay = new Date(viewYear, viewMonth + 1, 0);
    const startPad = firstDay.getDay();
    const days = [];

    for (let i = 0; i < startPad; i++) {
      days.push({ day: null, date: null });
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      days.push({ day: d, date: dateStr });
    }
    return days;
  }, [viewMonth, viewYear]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((evt) => {
      const d = evt.date?.slice(0, 10);
      if (d) {
        if (!map[d]) map[d] = [];
        map[d].push(evt);
      }
    });
    return map;
  }, [events]);

  const upcomingEvents = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return events.filter((e) => e.date >= today).slice(0, 10);
  }, [events]);

  const selectedEvents = selectedDate ? (eventsByDate[selectedDate] || []) : null;

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const formatDate = (d) => {
    const date = new Date(d + "T00:00:00");
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  return (
    <div className="min-h-screen bg-[#F0F4F3]">
      <Helmet>
        <title>Events & Schedule | Commonwealth FC Youth Soccer Charlottesville</title>
        <meta name="description" content="Commonwealth FC events calendar — tryouts, games, tournaments, and training sessions in Charlottesville, VA. Stay up to date with our youth soccer schedule." />
        <meta property="og:title" content="Events & Schedule | Commonwealth FC" />
        <meta property="og:description" content="Youth soccer events calendar — tryouts, games, tournaments in Charlottesville, VA." />
        <meta property="og:url" content="https://commonwealthfc.org/events" />
        <link rel="canonical" href="https://commonwealthfc.org/events" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "EventSeries",
            "name": "Commonwealth FC Events",
            "organizer": {
              "@type": "SportsOrganization",
              "name": "Commonwealth FC",
              "url": "https://commonwealthfc.org"
            },
            "location": {
              "@type": "Place",
              "name": "Charlottesville, VA"
            }
          }
        `}</script>
      </Helmet>

      {/* Nav */}
      <nav className="bg-[#0C2E3C] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            <img src={logoUrl} alt="Commonwealth FC" className="h-9 w-auto" />
            <span className="text-sm font-bold uppercase tracking-wider text-[#F0F4F3] hidden sm:block">Commonwealth FC</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/tryouts" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium transition-colors">Tryouts</Link>
            <Link to="/join" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium transition-colors">Join</Link>
            <Link to="/" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium transition-colors">Home</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0C2E3C] mb-2" data-testid="events-page-title">
          Events & Schedule
        </h1>
        <p className="text-[#0C2E3C]/60 mb-8">Upcoming tryouts, tournaments, and club events</p>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0C2E3C]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Calendar */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-sm border border-[#0C2E3C]/10 overflow-hidden" data-testid="events-calendar">
                {/* Month nav */}
                <div className="flex items-center justify-between px-5 py-4 bg-[#0C2E3C]">
                  <button onClick={prevMonth} className="text-[#C8E64E] hover:text-white transition-colors" data-testid="prev-month">
                    <ChevronLeft size={20} />
                  </button>
                  <h2 className="text-white font-heading font-bold text-lg tracking-wider">
                    {MONTH_NAMES[viewMonth]} {viewYear}
                  </h2>
                  <button onClick={nextMonth} className="text-[#C8E64E] hover:text-white transition-colors" data-testid="next-month">
                    <ChevronRight size={20} />
                  </button>
                </div>

                {/* Day headers */}
                <div className="grid grid-cols-7 border-b border-[#0C2E3C]/10">
                  {DAY_NAMES.map((d) => (
                    <div key={d} className="py-2 text-center text-[10px] font-bold uppercase tracking-wider text-[#0C2E3C]/50">
                      {d}
                    </div>
                  ))}
                </div>

                {/* Day cells */}
                <div className="grid grid-cols-7">
                  {calendarDays.map((cell, i) => {
                    const dayEvents = cell.date ? (eventsByDate[cell.date] || []) : [];
                    const isToday = cell.date === new Date().toISOString().slice(0, 10);
                    const isSelected = cell.date === selectedDate;
                    return (
                      <button
                        key={i}
                        onClick={() => cell.date && setSelectedDate(cell.date === selectedDate ? null : cell.date)}
                        disabled={!cell.day}
                        className={`relative min-h-[72px] p-1.5 border-b border-r border-[#0C2E3C]/5 text-left transition-colors
                          ${!cell.day ? "bg-[#F0F4F3]/50" : "hover:bg-[#C8E64E]/10 cursor-pointer"}
                          ${isToday ? "bg-[#C8E64E]/10" : ""}
                          ${isSelected ? "ring-2 ring-inset ring-[#0C2E3C]" : ""}
                        `}
                        data-testid={cell.date ? `calendar-day-${cell.date}` : undefined}
                      >
                        {cell.day && (
                          <>
                            <span className={`text-xs font-bold ${isToday ? "text-[#0C2E3C]" : "text-[#0C2E3C]/70"}`}>
                              {cell.day}
                            </span>
                            {dayEvents.length > 0 && (
                              <div className="mt-0.5 space-y-0.5">
                                {dayEvents.slice(0, 2).map((evt) => {
                                  const cfg = EVENT_TYPE_CONFIG[evt.event_type] || EVENT_TYPE_CONFIG.general;
                                  return (
                                    <div key={evt.event_id} className={`text-[9px] font-medium truncate px-1 py-0.5 rounded border ${cfg.color}`}>
                                      {evt.title}
                                    </div>
                                  );
                                })}
                                {dayEvents.length > 2 && (
                                  <span className="text-[9px] text-[#0C2E3C]/50 font-medium px-1">+{dayEvents.length - 2} more</span>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Selected date events */}
              {selectedEvents && (
                <div className="mt-4 space-y-3" data-testid="selected-date-events">
                  <h3 className="font-heading font-bold text-[#0C2E3C] text-lg">{formatDate(selectedDate)}</h3>
                  {selectedEvents.length === 0 ? (
                    <p className="text-sm text-[#0C2E3C]/50">No events on this date</p>
                  ) : (
                    selectedEvents.map((evt) => <EventCard key={evt.event_id} event={evt} />)
                  )}
                </div>
              )}
            </div>

            {/* Upcoming sidebar */}
            <div>
              <h3 className="font-heading font-bold text-[#0C2E3C] text-lg mb-4">Upcoming Events</h3>
              {upcomingEvents.length === 0 ? (
                <div className="bg-white rounded-sm border border-[#0C2E3C]/10 p-6 text-center">
                  <Calendar size={32} className="mx-auto text-[#0C2E3C]/20 mb-2" />
                  <p className="text-sm text-[#0C2E3C]/50">No upcoming events</p>
                  <p className="text-xs text-[#0C2E3C]/40 mt-1">Check back soon for tryouts and games</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="upcoming-events-list">
                  {upcomingEvents.map((evt) => <EventCard key={evt.event_id} event={evt} compact />)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-[#0C2E3C] mt-16 py-8 text-center">
        <p className="text-[#F0F4F3]/50 text-xs">Commonwealth FC &middot; Charlottesville, VA</p>
      </footer>
    </div>
  );
};

const EventCard = ({ event, compact }) => {
  const cfg = EVENT_TYPE_CONFIG[event.event_type] || EVENT_TYPE_CONFIG.general;
  return (
    <div className={`bg-white rounded-sm border border-[#0C2E3C]/10 overflow-hidden ${compact ? "p-3" : "p-4"}`} data-testid={`event-card-${event.event_id}`}>
      <div className="flex items-start gap-3">
        {!compact && (
          <div className="w-12 h-12 rounded-sm bg-[#0C2E3C] flex flex-col items-center justify-center flex-shrink-0">
            <span className="text-[10px] text-[#C8E64E] font-bold uppercase">
              {new Date(event.date + "T00:00:00").toLocaleDateString("en-US", { month: "short" })}
            </span>
            <span className="text-lg text-white font-bold leading-none">
              {new Date(event.date + "T00:00:00").getDate()}
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${cfg.color}`}>{cfg.label}</span>
            {event.age_groups?.length > 0 && (
              <span className="text-[9px] text-[#0C2E3C]/50 font-medium">{event.age_groups.join(", ")}</span>
            )}
          </div>
          <h4 className="font-heading font-bold text-[#0C2E3C] text-sm leading-tight">{event.title}</h4>
          {event.description && !compact && (
            <p className="text-xs text-[#0C2E3C]/60 mt-1 line-clamp-2">{event.description}</p>
          )}
          <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#0C2E3C]/50">
            <span className="flex items-center gap-1">
              <Calendar size={11} /> {new Date(event.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </span>
            {event.time && (
              <span className="flex items-center gap-1"><Clock size={11} /> {event.time}{event.end_time ? ` - ${event.end_time}` : ""}</span>
            )}
            {event.location && (
              <span className="flex items-center gap-1 truncate"><MapPin size={11} /> {event.location}</span>
            )}
          </div>
          {event.registration_url && (
            <a href={event.registration_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-[11px] font-bold text-[#0C2E3C] hover:text-[#C8E64E] transition-colors">
              Register <ExternalLink size={10} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventsPage;
